import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, User, ArrowLeft, Lock, Building2, Shield } from "lucide-react";
import { Link } from "wouter";
import type { Organization } from "@shared/schema";

const updateProfileSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(50, "First name must be 50 characters or less"),
  lastName: z.string().min(1, "Last name is required").max(50, "Last name must be 50 characters or less"),
  email: z.string().email("Please enter a valid email address"),
});

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(8, "New password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your new password"),
}).refine(d => d.newPassword === d.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type UpdateProfileForm = z.infer<typeof updateProfileSchema>;
type ChangePasswordForm = z.infer<typeof changePasswordSchema>;

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  viewer: "Viewer",
};

export default function AccountSettings() {
  const [, setLocation] = useLocation();
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const { toast } = useToast();
  const { user: currentUser, isAuthenticated, isLoading } = useAuth();
  const queryClient = useQueryClient();

  const profileForm = useForm<UpdateProfileForm>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      firstName: currentUser?.firstName || "",
      lastName: currentUser?.lastName || "",
      email: currentUser?.email || "",
    },
  });

  const passwordForm = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  useEffect(() => {
    if (currentUser) {
      profileForm.reset({
        firstName: currentUser.firstName || "",
        lastName: currentUser.lastName || "",
        email: currentUser.email || "",
      });
    }
  }, [currentUser, profileForm]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => { window.location.href = "/login"; }, 500);
    }
  }, [isAuthenticated, isLoading, toast]);

  useEffect(() => {
    if (currentUser && currentUser.role !== "admin" && currentUser.role !== "manager") {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access account settings.",
        variant: "destructive",
      });
      setLocation("/");
    }
  }, [currentUser, toast, setLocation]);

  const { data: organization } = useQuery<Organization>({
    queryKey: ["/api/organizations", currentUser?.organizationId],
    queryFn: async () => {
      const res = await fetch(`/api/organizations/${currentUser!.organizationId}`);
      if (!res.ok) throw new Error("Failed to fetch organization");
      return res.json();
    },
    enabled: !!currentUser?.organizationId,
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: UpdateProfileForm) => {
      const response = await fetch("/api/auth/user/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update profile");
      }
      return response.json();
    },
    onSuccess: () => {
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: "Profile updated", description: "Your account information has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data: ChangePasswordForm) => {
      const response = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: data.currentPassword, newPassword: data.newPassword }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update password");
      }
      return response.json();
    },
    onSuccess: () => {
      setPasswordSuccess(true);
      setPasswordError("");
      passwordForm.reset();
      setTimeout(() => setPasswordSuccess(false), 3000);
    },
    onError: (error: Error) => {
      setPasswordError(error.message);
      setPasswordSuccess(false);
    },
  });

  if (isLoading || !isAuthenticated || !currentUser) return null;
  if (currentUser.role !== "admin" && currentUser.role !== "manager") return null;

  const orgForHeader: Organization = organization || {
    id: currentUser.organizationId,
    name: "—",
    slug: "",
    createdAt: new Date(),
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader organization={orgForHeader} currentUser={currentUser} />

      <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Link href="/">
              <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" />
              </button>
            </Link>
            <div>
              <h1 className="text-lg font-medium text-foreground">Account Settings</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Manage your profile and preferences</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Profile card */}
            <Card className="border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  Profile
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...profileForm}>
                  <form onSubmit={profileForm.handleSubmit((data) => updateProfileMutation.mutate(data))} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        control={profileForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs text-muted-foreground">First Name</FormLabel>
                            <FormControl>
                              <Input placeholder="First name" {...field} className="h-9" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={profileForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs text-muted-foreground">Last Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Last name" {...field} className="h-9" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={profileForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="Email address" {...field} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {profileSuccess && (
                      <Alert className="border-green-500/20 bg-green-500/5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        <AlertDescription className="text-green-600 dark:text-green-400 text-sm">
                          Profile updated successfully.
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button type="submit" size="sm" className="h-8" disabled={updateProfileMutation.isPending}>
                      {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {/* Security / change password card */}
            <Card className="border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  Security
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...passwordForm}>
                  <form onSubmit={passwordForm.handleSubmit((data) => {
                    setPasswordError("");
                    changePasswordMutation.mutate(data);
                  })} className="space-y-4">
                    {/* Hidden username field lets password managers associate the new password with this account */}
                    <input type="hidden" autoComplete="username" value={currentUser?.email || ""} readOnly />
                    <FormField
                      control={passwordForm.control}
                      name="currentPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">Current password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••••" autoComplete="current-password" {...field} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        control={passwordForm.control}
                        name="newPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs text-muted-foreground">New password</FormLabel>
                            <FormControl>
                              <Input type="password" placeholder="At least 8 characters" autoComplete="new-password" {...field} className="h-9" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={passwordForm.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs text-muted-foreground">Confirm new password</FormLabel>
                            <FormControl>
                              <Input type="password" placeholder="••••••••" autoComplete="new-password" {...field} className="h-9" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {passwordError && (
                      <Alert variant="destructive" className="py-2">
                        <AlertDescription className="text-sm">{passwordError}</AlertDescription>
                      </Alert>
                    )}

                    {passwordSuccess && (
                      <Alert className="border-green-500/20 bg-green-500/5 py-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        <AlertDescription className="text-green-600 dark:text-green-400 text-sm">
                          Password updated successfully.
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button type="submit" size="sm" className="h-8" disabled={changePasswordMutation.isPending}>
                      {changePasswordMutation.isPending ? "Updating..." : "Update Password"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {/* Organization & role card */}
            <Card className="border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Organization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-xs text-muted-foreground">Organization</span>
                    <span className="text-sm font-medium">
                      {organization ? organization.name : <span className="text-muted-foreground">Loading…</span>}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs text-muted-foreground">Your role</span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-accent text-accent-foreground">
                      <Shield className="h-3 w-3" />
                      {ROLE_LABELS[currentUser.role || ""] || currentUser.role}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
